/**
 * A person's avatar everywhere outside the game world (SC-270): their profile photo, or, with no
 * photo or one that fails to load, the generated pattern in the style they picked. Never initials:
 * those are for the in-game meeting tiles (LiveKitVideoDisplay/cameraOffPicture.tsx).
 *
 * The pattern is seeded by the user id, not a name, so a person looks the same in every list and
 * keeps their look after a rename. The Edit Profile style picker uses this too, so its preview
 * matches what others see.
 */

import React, { useEffect, useState } from 'react'
import BoringAvatar from 'boring-avatars'
import type { UserData } from '../types/user'
import { getDisplayName } from '../utils/personName'

export const AVATAR_PATTERN_STYLES = ['beam', 'pixel', 'sunset', 'ring', 'marble', 'bauhaus'] as const
export type AvatarPatternStyle = (typeof AVATAR_PATTERN_STYLES)[number]

/** Profile `avatar` holds 'custom' while a photo is set; anything that isn't a style draws as 'beam'. */
export function patternStyleOf(value: string | null | undefined): AvatarPatternStyle {
  return AVATAR_PATTERN_STYLES.includes(value as AvatarPatternStyle) ? (value as AvatarPatternStyle) : 'beam'
}

/** A photo the browser may load: http(s), or a local preview (`blob:`) in Edit Profile. */
function displayablePhoto(url: string | null | undefined): string | undefined {
  return typeof url === 'string' && /^(https?:\/\/|blob:)/i.test(url) ? url : undefined
}

export interface ProfileAvatarProps extends Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  /** Seeds the pattern; falls back to `name` only where no id is known. */
  userId?: string | null
  /** Accessible name. Wrap in an MUI Tooltip to show it on hover. */
  name: string
  photoUrl?: string | null
  /** Profile `avatar`: the pattern style. */
  patternStyle?: string | null
  size?: number
}

/** Forwards its ref and event props, so it can sit directly inside an MUI Tooltip or Chip. */
export const ProfileAvatar = React.forwardRef<HTMLElement, ProfileAvatarProps>(function ProfileAvatar(
  { userId, name, photoUrl, patternStyle, size = 40, style, ...rest },
  ref
) {
  const photo = displayablePhoto(photoUrl)
  const [failedPhoto, setFailedPhoto] = useState<string | undefined>(undefined)
  useEffect(() => setFailedPhoto(undefined), [photo])

  // The box MUI Avatar had, which the call sites were laid out around: a block-level flex box
  // (no baseline gap under it) that is positioned, so decorations drawn behind it stay behind.
  const frame: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    width: size,
    height: size,
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
    ...style,
  }

  if (photo && failedPhoto !== photo) {
    return (
      <img
        {...rest}
        ref={ref as React.Ref<HTMLImageElement>}
        src={photo}
        alt={name}
        data-testid="profile-avatar-photo"
        style={{ ...frame, objectFit: 'cover' }}
        onError={() => setFailedPhoto(photo)}
      />
    )
  }

  return (
    <span
      {...rest}
      ref={ref as React.Ref<HTMLSpanElement>}
      role="img"
      aria-label={name}
      style={frame}
      data-testid="profile-avatar-pattern"
    >
      <BoringAvatar size={size} name={userId || name || '?'} variant={patternStyleOf(patternStyle)} />
    </span>
  )
})

/** Props for the signed-in user, straight from Redux. */
export function profileAvatarPropsOf(
  user: Pick<UserData, 'id' | 'username' | 'firstName' | 'lastName' | 'displayName' | 'avatar' | 'customAvatarUrl'> | null | undefined
): Pick<ProfileAvatarProps, 'userId' | 'name' | 'photoUrl' | 'patternStyle'> {
  const name = getDisplayName(user)
  return { userId: user?.id, name, photoUrl: user?.customAvatarUrl, patternStyle: user?.avatar }
}

export default ProfileAvatar
